<script lang="ts" setup>
import { ref, useId, useSlots, useTemplateRef } from 'vue';
import { useMagicKeys, useResizeObserver, whenever } from '@vueuse/core';
import VSizeContainer, {
  type ContainerSize,
} from '@shared/layout/VSizeContainer/VSizeContainer.vue';
import VButtonGroup from '@shared/component/VButtonGroup/VButtonGroup.vue';
import { useApplicationRoot } from '@tools/applicationRoot';
import { useScrollLock } from '@shared/layout/useScrollLock';
import VButton from './VButtonModal.vue';
import { type ButtonType } from '../VButton/vButtonTypes';
import { ModalType } from '../types';

export interface VModalProps {
  isActive?: boolean;
  disabled?: boolean;
  type?: ModalType;
  /**
   * Bevat een korte tekst die matcht met de rest van de modalteksten.
   *
   * Gebruik `props.type` om een passend visueel onderscheidend uiterlijk in te stellen.
   * @example 'Verwijderen'
   */
  confirmButtonText?: string;
  cancelButtonText?: string;
  denyButtonText?: string;
  loading?: boolean;
  overflow?: boolean;
  /**
   * Kort en meestal in vragende vorm.
   * @example 'Product verwijderen?'
   */
  title?: string;
  invertHeader?: boolean;
  dataTestid?: string;
  size?: ContainerSize;
  isIframe?: boolean;
  /**
   * Schakelt de standaard padding uit voor custom layouts
   */
  noPadding?: boolean;
  /**
   * Verticale uitlijning van de modal: gecentreerd (standaard) of bovenaan.
   * Combineer `'top'` met een eigen `class` (bijv. `class="pt-24"`) voor de gewenste marge —
   * die class valt door naar dezelfde overlay-container.
   */
  align?: 'center' | 'top';
  /**
   * Laat de inhoud de beschikbare hoogte vullen en zélf scrollen (i.p.v. de modal-sectie).
   * Handig voor bv. een tabel met sticky kop binnen een vaste modal-chrome.
   */
  fillContent?: boolean;
}

const props = withDefaults(defineProps<VModalProps>(), {
  dataTestId: undefined,
  isActive: false,
  disabled: false,
  type: ModalType.Success,
  confirmButtonText: 'Opslaan',
  cancelButtonText: 'Annuleren',
  denyButtonText: '',
  overflow: false,
  invertHeader: false,
  noPadding: false,
  align: 'center',
  fillContent: false,
  size: 'deprecated_768',
});

const emit = defineEmits<{
  closeModal: [value?: Event];
  close: [value?: Event];
  confirmAction: [value?: Event];
  denyAction: [value?: Event];
  cancelButtonClicked: [value?: Event];
}>();

defineSlots<{
  /**
   * Legt uit wat de gevolgen zijn en/of bevat aanvullende informatie.
   * @example "Als je Voorbeeldproduct verwijdert, kan dit niet ongedaan gemaakt worden. Je collega's ontvangen hiervan een melding per e-mail."
   */
  default(): any;
  footer(): any;
  /**
   * Volledige controle over de header content. Wanneer gebruikt, worden title en header-end genegeerd.
   */
  header(): any;
  'header-end'(): any;
}>();

useScrollLock(() => props.isActive);

const applicationroot = useApplicationRoot();

const slots = useSlots();
const keys = useMagicKeys();
const titleId = useId();
whenever(keys.escape, () => close());

function close() {
  emit('closeModal');
  emit('close');
}

const getButtonType = (): ButtonType => {
  switch (props.type) {
    case ModalType.Danger:
      return 'danger';
    case ModalType.Success:
    default:
      return 'primary';
  }
};

function cancel() {
  emit('cancelButtonClicked');
  close();
}

const sectionRef = useTemplateRef('sectionRef');
const contentRef = useTemplateRef('contentRef');
const isContentScrollable = ref(false);

function updateScrollable() {
  const el = sectionRef.value;
  if (!el) {
    isContentScrollable.value = false;
    return;
  }
  isContentScrollable.value = el.scrollHeight - el.clientHeight > 0;
}

useResizeObserver(sectionRef, updateScrollable);
useResizeObserver(contentRef, updateScrollable);
</script>

<template>
  <Teleport :to="applicationroot" defer>
    <div
      v-show="props.isActive"
      class="fixed inset-0 z-overlay grid min-w-[50%] animate-fade animation-duration-300"
      :class="props.align === 'top' ? 'place-items-start pt-24' : 'place-items-center'"
      :data-testid="props.dataTestid"
      role="dialog"
      :aria-labelledby="titleId"
    >
      <button
        type="button"
        class="absolute inset-0 cursor-auto bg-grey-800/80"
        @click="close()"
      ></button>
      <VSizeContainer v-if="!props.isIframe" :size="props.size" class="z-dialog">
        <div
          class="grid grid-rows-[auto_minmax(0,1fr)_auto] rounded-md border border-solid border-grey-200 bg-white"
          :class="{
            'overflow-visible': props.overflow,
            'overflow-hidden': !props.overflow,
            'max-h-dvh': props.align !== 'top',
            'max-h-[calc(100dvh_-_8rem)]': props.align === 'top',
          }"
        >
          <header
            v-if="slots.header || slots['header-end'] || props.title"
            :id="titleId"
            class="rounded-t-md border-0 border-b border-solid border-b-grey-200 px-4 py-4 text-lg lg:px-8"
            :class="{ 'bg-grey-100': !invertHeader }"
          >
            <slot v-if="slots.header" name="header"></slot>
            <div v-else class="flex flex-wrap justify-between gap-4">
              <span
                v-if="props.title"
                class="text-sm font-bold"
                :class="{ 'text-primary-900': invertHeader, 'text-primary': !invertHeader }"
              >
                {{ props.title }}
              </span>
              <slot name="header-end"></slot>
            </div>
          </header>

          <section
            ref="sectionRef"
            :class="[
              !props.noPadding && 'p-4 lg:p-8',
              props.fillContent
                ? 'flex min-h-0 flex-col overflow-hidden'
                : {
                    'overflow-visible': props.overflow,
                    'overflow-auto': !props.overflow,
                  },
            ]"
          >
            <div ref="contentRef" :class="props.fillContent && 'flex min-h-0 flex-1 flex-col'">
              <slot></slot>
            </div>
          </section>

          <footer
            class="flex justify-end gap-2"
            :class="['p-4', 'lg:px-8', { 'border-t border-grey-200': isContentScrollable }]"
          >
            <slot v-if="slots.footer" name="footer"></slot>
            <VButtonGroup v-else>
              <VButton inverted v-if="props.cancelButtonText" @click.stop="cancel">
                {{ props.cancelButtonText }}
              </VButton>
              <VButton
                :disabled="props.disabled"
                v-if="props.denyButtonText"
                @click="emit('denyAction')"
                type="danger"
              >
                {{ props.denyButtonText }}
              </VButton>
              <VButton
                v-if="props.confirmButtonText"
                :disabled="props.disabled"
                :type="getButtonType()"
                :loading="props.loading"
                @click.stop="emit('confirmAction')"
              >
                {{ props.confirmButtonText }}
              </VButton>
            </VButtonGroup>
          </footer>
        </div>
      </VSizeContainer>

      <div
        v-else
        class="z-dialog mx-8 h-[calc(80dvh-77px)] w-full max-w-[90dvw] rounded-t bg-white shadow-lg"
      >
        <slot></slot>
      </div>
    </div>
  </Teleport>
</template>
